import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function ForgotPassword() {
  const { branding } = useTheme();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // R268dddd · Feb 2026 — Surface the backend error so users
      // aren't left staring at a silent form when SMTP / DB is down.
      // The endpoint still hides "user not found" behind a 200 to
      // prevent enumeration, so a 4xx/5xx here is a real problem.
      toast.error(apiErrorMessage(err, "We couldn't send the reset link. Please try again in a minute."));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-16">
      <div className="max-w-lg w-full" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs text-foreground/60 hover:text-foreground mb-8" data-testid="forgot-back-login">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to log in
        </Link>
        {sent ? (
          <div className="border border-border p-10" data-testid="forgot-sent-screen">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div className="label-eyebrow mb-2">Check your inbox</div>
            <h1 className="heading-display text-3xl mb-4">Reset link sent.</h1>
            <p className="text-sm text-foreground/80 leading-relaxed">
              If an account exists for <strong>{email}</strong>, we&apos;ve sent a password-reset link.
              The link expires in 60 minutes. Don&apos;t forget to check spam.
            </p>
            <Link to="/login"><Button className="rounded-none mt-6" data-testid="forgot-back-login-cta">Return to log in</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-border p-10">
            <div className="label-eyebrow mb-2">{branding.platform_name}</div>
            <h1 className="heading-display text-3xl mb-2">Forgot your password?</h1>
            <p className="text-sm text-foreground/70 mb-8">Enter the email you signed up with and we&apos;ll send you a reset link.</p>
            <div className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 rounded-none font-bold uppercase tracking-widest text-xs" data-testid="forgot-submit-btn">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
